// RunFlowLinks — the per-run map (linked runs phase 6; T9): what fed this run,
// the run, and what it fed, left to right. Tap a run to open it. Read-only,
// built from tips and bins; it renders nothing on a run that links to no
// other run.

import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { ArrowRight, GitBranch } from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { fetchAllPaged } from '@/lib/fetchAllPaged'
import { useActiveTenantId } from '@/hooks/useActiveTenantId'
import { readState } from '@/lib/readState'
import { fmtKg } from '@/lib/runsKpis'
import { fedBy, fedInto, linksAnotherRun, type InTip, type OutTip, type MadeBin, type RunNode, type FedBy, type FedInto } from '@/lib/runFlowLinks'

type Page<T> = PromiseLike<{ data: T[] | null; error: { message: string } | null }>

function useRunLinks(runId: string) {
  const tenantId = useActiveTenantId()
  return useQuery({
    queryKey: ['run-flow-links', tenantId, runId],
    queryFn: async () => {
      const [ins, outs, made] = await Promise.all([
        fetchAllPaged<InTip>((lo, hi) => supabase.from('tipping_log')
          .select('tipping_id, bin_quantity, net_weight_kg, delivery_id, bins:bin_id(source_run_id, runs:source_run_id(run_number, run_kind))')
          .eq('run_id', runId).order('tipping_id').range(lo, hi) as unknown as Page<InTip>),
        // The tips of the bulk-sort pallets this run made (crate-tracked bins),
        // onto runs that are not deleted. bins!inner / runs!inner filter on them.
        fetchAllPaged<OutTip>((lo, hi) => supabase.from('tipping_log')
          .select('tipping_id, run_id, bin_quantity, net_weight_kg, runs!inner(run_number, deleted_at), bins!inner(source_run_id, crate_count)')
          .eq('bins.source_run_id', runId).not('bins.crate_count', 'is', null).is('runs.deleted_at', null)
          .order('tipping_id').range(lo, hi) as unknown as Page<OutTip>),
        fetchAllPaged<MadeBin>((lo, hi) => supabase.from('bins')
          .select('bin_id, crate_count, crates_remaining, net_weight_kg, status')
          .eq('source_run_id', runId).not('crate_count', 'is', null).is('deleted_at', null)
          .order('bin_id').range(lo, hi) as unknown as Page<MadeBin>),
      ])
      for (const r of [ins, outs, made]) if (r.error) throw new Error(r.error)
      return { by: fedBy(ins.rows), into: fedInto(outs.rows, made.rows) }
    },
  })
}

const kg = fmtKg

function RunBox({ node, onOpen }: { node: RunNode; onOpen: (id: string) => void }) {
  return (
    <button type="button" onClick={() => onOpen(node.runId)}
      className="tap-target-y w-full rounded-lg border border-line bg-surface p-2 text-left hover:border-line-strong">
      <span className="block text-sm font-semibold text-fg">{node.runNumber}</span>
      <span className="block text-caption text-fg-secondary tabular-nums">
        {node.runKind === 'BULK_SORT' ? 'Bulk sort · ' : ''}{node.count} · {kg(node.kg)}
      </span>
    </button>
  )
}

export default function RunFlowLinks({ run }: {
  run: { run_id: string; run_number: string; run_kind?: string | null }
}) {
  const navigate = useNavigate()
  const onOpenRun = (id: string) => navigate(`/runs/${id}`)
  const q = useRunLinks(run.run_id)
  const state = readState(q)
  if (state === 'loading') return null
  if (state === 'unread') {
    return <p className="card text-caption text-fg-muted">Couldn&apos;t read how this run links. Check the connection and open the run again.</p>
  }
  if (!q.data || !linksAnotherRun(q.data.by, q.data.into)) return null
  const bulk = run.run_kind === 'BULK_SORT'
  return (
    <div className="card space-y-2">
      <p className="text-caption text-fg-muted flex items-center gap-1.5"><GitBranch size={14} /> How this run links</p>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_auto_1fr_auto_1fr] sm:items-center">
        <FedByColumn by={q.data.by} onOpen={onOpenRun} />
        <ArrowRight size={16} className="hidden sm:block text-fg-muted" aria-hidden />
        <div className="rounded-lg border-2 border-accent p-2 text-center">
          <span className="block text-sm font-semibold text-fg">{run.run_number}</span>
          <span className="block text-caption text-fg-secondary">{bulk ? 'Bulk sort' : 'This run'}</span>
        </div>
        <ArrowRight size={16} className="hidden sm:block text-fg-muted" aria-hidden />
        <FedIntoColumn into={q.data.into} bulk={bulk} onOpen={onOpenRun} />
      </div>
    </div>
  )
}

function FedByColumn({ by, onOpen }: { by: FedBy; onOpen: (id: string) => void }) {
  return (
    <div className="space-y-1.5">
      <p className="text-caption text-fg-muted">Fed by</p>
      {by.deliveries.count > 0 && (
        <p className="rounded-lg border border-line p-2 text-caption text-fg-secondary tabular-nums">
          Deliveries · {by.deliveries.count} · {kg(by.deliveries.kg)}
        </p>
      )}
      {by.runs.map(r => <RunBox key={r.runId} node={r} onOpen={onOpen} />)}
    </div>
  )
}

function FedIntoColumn({ into, bulk, onOpen }: { into: FedInto; bulk: boolean; onOpen: (id: string) => void }) {
  const { pallets, crates } = into.inStock
  return (
    <div className="space-y-1.5">
      <p className="text-caption text-fg-muted">Fed into</p>
      {into.runs.length === 0 && pallets === 0 && (
        <p className="text-caption text-fg-muted">{bulk ? 'Nothing yet.' : 'Nothing yet: a packing line makes final pallets.'}</p>
      )}
      {into.runs.map(r => <RunBox key={r.runId} node={r} onOpen={onOpen} />)}
      {pallets > 0 && (
        <p className="rounded-lg border border-dashed border-line p-2 text-caption text-fg-secondary tabular-nums">
          In stock · {pallets} pallet{pallets === 1 ? '' : 's'} · {crates} crates · {kg(into.inStock.kg)}
        </p>
      )}
    </div>
  )
}
